<template>
    <div id="imports-history-page">
        <div class="container-fluid">
            <div class="d-flex justify-content-between flex-wrap gap-md align-items-center">
                <div class="d-flex flex-column">
                    <div class="kv-title">{{ t('imports-title') }}</div>
                    <p class="text-muted mb-0">{{ t('imports-subtitle') }}</p>
                </div>

                <KvPopup class="header-import" placement="bottom left">
                    <template #trigger="{ toggle }">
                        <KvButton prepend-icon="ui-upload-02" variant="filled" size="large" icon-size="4" @click="toggle">
                            {{ t('imports-importData') }}
                        </KvButton>
                    </template>

                    <template #content="{ toggle }">
                        <span class="kv-popup-title">{{ t('imports-chooserSubtitle') }}</span>
                        <div class="kv-popup-list">
                            <KvButton alignment="left" prepend-icon="ui-upload-02" variant="outlined"
                                :title="t('imports-newOrders')" @click="chooseOrders(); toggle()">
                                {{ t('imports-newOrders') }}
                            </KvButton>
                            <KvButton alignment="left" prepend-icon="ui-upload-02" variant="outlined"
                                :title="t('imports-newClients')" @click="chooseClients(); toggle()">
                                {{ t('imports-newClients') }}
                            </KvButton>
                            <KvButton v-for="imp in importers" :key="imp.id" alignment="left" prepend-icon="ui-upload-02"
                                variant="outlined" :title="imp.name" @click="chooseImporter(imp); toggle()">
                                {{ imp.name }}
                            </KvButton>
                            <KvButton v-for="p in backfillPlugins" :key="'bf-' + p.id" alignment="left"
                                prepend-icon="ui-calendar" variant="outlined"
                                :title="t('imports-backfillButton', [pluginLabel(p)])"
                                @click="chooseBackfill(p); toggle()">
                                {{ t('imports-backfillButton', [pluginLabel(p)]) }}
                            </KvButton>
                        </div>
                    </template>
                </KvPopup>
            </div>

            <div style="margin-top: 15px; margin-bottom: 15px;">
                <KvFilter v-show="ready" ref="filter-bar" @changed="updateFilters" :show-labels="true"
                    :has-period-filter="false" :has-branch-filter="false" :filters="extraFilters" />
            </div>

            <importOrders ref="importOrders" @queued="onImportQueued"><span></span></importOrders>
            <importClients ref="importClients" @queued="onImportQueued"><span></span></importClients>
            <pluginImporter :importer="currentImporter" ref="pluginImporter" @queued="onImportQueued"><span></span></pluginImporter>

            <modal id="backfillModal" ref="backfill">
                <template slot="title">{{ t('imports-backfillTitle') }}</template>
                <template slot="body">
                    <p v-if="backfillPluginLabel"><strong>{{ backfillPluginLabel }}</strong></p>
                    <p class="text-muted">{{ t('imports-backfillSubtitle') }}</p>
                    <div class="field mt-2">
                        <label class="d-block">{{ t('imports-filterDateFrom') }}</label>
                        <KvDatePicker v-model="backfillStart" value-format="yyyy-MM-dd"
                            :placeholder="t('imports-filterDateFrom')" />
                    </div>
                    <div class="field mt-2">
                        <label class="d-block">{{ t('imports-filterDateTo') }}</label>
                        <KvDatePicker v-model="backfillEnd" value-format="yyyy-MM-dd"
                            :placeholder="t('imports-filterDateTo')" />
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-light" @click="$refs.backfill.close()">{{ t('imports-close') }}</button>
                    <button class="btn btn-success" :disabled="!canStartBackfill" @click="startBackfill">
                        {{ t('imports-backfillStart') }}
                    </button>
                </template>
            </modal>

            <confirm id="confirmCancelImport" ref="confirmCancel" @confirm="cancelImport" buttonClass="btn-danger">
                <template slot="title">
                    <span>{{ t('imports-cancelConfirmTitle') }}</span>
                </template>
                <template slot="body">
                    <div>{{ t('imports-cancelConfirmBody') }}</div>
                </template>
            </confirm>

            <modal id="importDetails" ref="details">
                <template slot="title">{{ t('imports-detailsTitle') }}</template>
                <template slot="body">
                    <div v-if="detail">
                        <table class="table">
                            <tr>
                                <td>{{ t('imports-columnType') }}</td>
                                <td>{{ typeLabel(detail.type) }}</td>
                            </tr>
                            <tr v-if="detail.type === 'plugin_file' && detail.plugin_label">
                                <td>{{ t('imports-columnPlugin') }}</td>
                                <td>{{ detail.plugin_label }}</td>
                            </tr>
                            <tr v-if="detail.type === 'plugin_file' && detail.importer_label">
                                <td>{{ t('imports-columnImporter') }}</td>
                                <td>{{ detail.importer_label }}</td>
                            </tr>
                            <tr>
                                <td>{{ t('imports-columnFilename') }}</td>
                                <td>{{ detail.original_filename || '-' }}</td>
                            </tr>
                            <tr>
                                <td>{{ t('imports-columnUser') }}</td>
                                <td>{{ (detail.user && detail.user.name) || '-' }}</td>
                            </tr>
                            <tr>
                                <td>{{ t('imports-columnBranch') }}</td>
                                <td>{{ (detail.branch && detail.branch.title) || '-' }}</td>
                            </tr>
                            <tr v-if="detail.type === 'plugin_history'">
                                <td>{{ t('imports-summaryImportedOrders') }}</td>
                                <td>{{ detail.counts.imported_orders }}</td>
                            </tr>
                            <template v-else>
                                <tr>
                                    <td>{{ t('imports-summaryNewOrders') }}</td>
                                    <td>{{ detail.counts.new_orders }}</td>
                                </tr>
                                <tr>
                                    <td>{{ t('imports-summaryUpdatedOrders') }}</td>
                                    <td>{{ detail.counts.updated_orders }}</td>
                                </tr>
                                <tr>
                                    <td>{{ t('imports-summaryNewClients') }}</td>
                                    <td>{{ detail.counts.new_clients }}</td>
                                </tr>
                                <tr>
                                    <td>{{ t('imports-summaryUpdatedClients') }}</td>
                                    <td>{{ detail.counts.updated_clients }}</td>
                                </tr>
                            </template>
                            <tr>
                                <td>{{ t('imports-summaryFailed') }}</td>
                                <td>
                                    {{ detail.counts.failed }}
                                    <a v-if="detail.failed_rows && detail.failed_rows.length"
                                        href="#" @click.prevent="downloadFailedRows">{{ t('imports-downloadFailed') }}</a>
                                </td>
                            </tr>
                            <tr v-if="detail.errors">
                                <td>{{ t('imports-columnError') }}</td>
                                <td class="text-danger">{{ detail.errors }}</td>
                            </tr>
                        </table>
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-primary" @click="$refs.details.close()">{{ t('imports-close') }}</button>
                </template>
            </modal>

            <grid ref="grid" source="import-history" :filters="filters" :show-search="false" @loaded="onLoaded">
                <template v-slot:rows="grid">
                    <thead>
                        <tr>
                            <grid-th :grid="grid">{{ t('imports-columnType') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnFilename') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnUser') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnBranch') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnStatus') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnProgress') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnDate') }}</grid-th>
                            <grid-th :grid="grid">{{ t('imports-columnActions') }}</grid-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in grid.data" :key="item.id">
                            <td>{{ typeLabel(item.type) }}</td>
                            <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis;">
                                {{ item.original_filename || '-' }}
                            </td>
                            <td>{{ (item.user && item.user.name) || '-' }}</td>
                            <td>{{ (item.branch && item.branch.title) || '-' }}</td>
                            <td><status-flag :status="item.status" /></td>
                            <td style="min-width: 140px">
                                <span v-if="item.indeterminate" class="text-primary">
                                    <span class="far fa-spinner fa-spin"></span>
                                    {{ t('imports-preparing') }}
                                </span>
                                <progressBar v-else :progress="Number(item.progress) || 0" />
                            </td>
                            <td style="min-width: 150px">
                                <human-date :date="item.created_at" format="[d]/[M]/[Y] [h]:[m]" />
                            </td>
                            <td style="white-space: nowrap;">
                                <button v-if="item.has_results" class="btn btn-sm btn-light"
                                    @click.prevent="openDetails(item.id)">
                                    {{ t('imports-details') }}
                                </button>
                                <span v-else-if="!item.cancellable" class="text-muted" style="font-size: 12px;">
                                    {{ t('imports-noData') }}
                                </span>
                                <button v-if="item.cancellable" class="btn btn-sm btn-danger"
                                    @click.prevent="makeCancelRequest(item.id)">
                                    {{ t('imports-cancel') }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </grid>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi.js";
import statusFlag from "./status-flag.vue";
import progressBar from "./progress-bar.vue";
import importOrders from "../clients/import.vue";
import importClients from "../clients/import-clients.vue";
import pluginImporter from "../clients/plugin-importer.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvFilter from "../components/reports/kv-filter-bar.vue";
import KvDatePicker from "../components/inputs/kv-date-picker.vue";

export default {
    components: {
        statusFlag,
        progressBar,
        importOrders,
        importClients,
        pluginImporter,
        KvButton,
        KvPopup,
        KvFilter,
        KvDatePicker,
    },
    data() {
        return {
            ready: false,
            filters: {},
            extraFilters: [],
            idToCancel: null,
            detail: null,
            pollTimer: null,
            importers: [],
            currentImporter: {},
            plugins: [],
            backfillPluginId: "",
            backfillPluginLabel: "",
            backfillStart: "",
            backfillEnd: "",
        };
    },
    computed: {
        user() {
            return this.$store.state.user;
        },
        isAdmin() {
            return !!(this.user && this.user.global_admin);
        },
        canStartBackfill() {
            return !!(this.backfillPluginId && this.backfillStart && this.backfillEnd);
        },
        backfillPlugins() {
            // Only plugins that actually implement date-range history imports.
            return this.plugins.filter((p) => p.supports_history);
        },
    },
    mounted() {
        const configs = this.user && this.user.permissions && this.user.permissions.configs;
        if (!configs || configs.enabled != 1) {
            this.$router.replace("/");
            return;
        }
        this.buildFilters();
        this.loadStarters();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        buildFilters() {
            this.extraFilters = [
                {
                    component: "kv-select",
                    name: "type",
                    label: this.t("imports-filterType"),
                    removable: true,
                    options: {
                        options: [
                            { value: "orders", label: this.t("imports-typeOrders") },
                            { value: "clients", label: this.t("imports-typeClients") },
                            { value: "plugin_file", label: this.t("imports-typePluginFile") },
                            { value: "plugin_history", label: this.t("imports-typePluginHistory") },
                        ],
                    },
                },
                {
                    component: "kv-select",
                    name: "status",
                    label: this.t("imports-filterStatus"),
                    removable: true,
                    options: {
                        options: [
                            { value: "pending", label: this.t("importHistory-statusPending") },
                            { value: "in_progress", label: this.t("importHistory-statusInProgress") },
                            { value: "complete", label: this.t("importHistory-statusComplete") },
                            { value: "cancelled", label: this.t("importHistory-statusCancelled") },
                            { value: "failed", label: this.t("importHistory-statusFailed") },
                        ],
                    },
                },
                {
                    component: "period",
                    name: "period",
                    label: this.t("imports-filterPeriod"),
                    removable: true,
                    options: {},
                },
            ];
            this.ready = true;
        },
        async loadStarters() {
            // Plugin file importers are available to anyone with settings access.
            try {
                const importersResponse = await this.$http(
                    keviApi.getOptions("clients/plugin-importers", "GET"),
                );
                this.importers = importersResponse.body || [];
            } catch (e) {
                this.importers = [];
            }
            // Date-range history imports are admin-only on the backend, so only fetch the
            // installed-plugin list (also admin-gated) when the user is a global admin.
            if (this.isAdmin) {
                try {
                    const pluginsResponse = await this.$http(
                        keviApi.getOptions("plugins/list", "GET"),
                    );
                    this.plugins = (pluginsResponse.body && pluginsResponse.body.data) || [];
                } catch (e) {
                    this.plugins = [];
                }
            }
        },
        refreshGrid() {
            if (this.$refs.grid) {
                this.$refs.grid.update();
            }
        },
        chooseOrders() {
            this.$nextTick(() => this.$refs.importOrders.$refs.import.open());
        },
        chooseClients() {
            this.$nextTick(() => this.$refs.importClients.$refs.importClients.open());
        },
        chooseImporter(importer) {
            this.currentImporter = importer;
            this.$nextTick(() => this.$refs.pluginImporter.openModal());
        },
        chooseBackfill(plugin) {
            this.backfillPluginId = plugin.id;
            this.backfillPluginLabel = this.pluginLabel(plugin);
            this.backfillStart = "";
            this.backfillEnd = new Date().toISOString().slice(0, 10);
            this.$nextTick(() => this.$refs.backfill.open());
        },
        pluginLabel(plugin) {
            return plugin.name || plugin.plugin || "#" + plugin.id;
        },
        startBackfill() {
            if (!this.canStartBackfill) {
                return;
            }
            const options = keviApi.getOptions(
                "plugins/" + this.backfillPluginId + "/import-history/add",
                "POST",
                { date_start: this.backfillStart, date_end: this.backfillEnd },
            );
            this.$http(options).then(
                (response) => {
                    if (response.status === 201 || response.status === 200) {
                        this.$store.dispatch("notification/success", this.t("imports-backfillStarted"));
                        this.$refs.backfill.close();
                        this.refreshGrid();
                    } else {
                        this.$store.dispatch("notification/warning", this.t("imports-backfillError"));
                    }
                },
                () => {
                    this.$store.dispatch("notification/warning", this.t("imports-backfillError"));
                },
            );
        },
        onImportQueued() {
            this.$store.dispatch("notification/success", this.t("imports-queuedNotice"));
            this.refreshGrid();
        },
        updateFilters(values) {
            // The modern filter bar emits values keyed by filter name; map them onto the
            // grid/backend filter shape (type/status/date_from/date_to).
            const period = values.period || null;
            this.filters = {
                type: values.type || undefined,
                status: values.status || undefined,
                date_from: period && period.start ? period.start : undefined,
                date_to: period && period.end ? period.end : undefined,
            };
        },
        typeLabel(type) {
            const map = {
                orders: this.t("imports-typeOrders"),
                clients: this.t("imports-typeClients"),
                plugin_file: this.t("imports-typePluginFile"),
                plugin_history: this.t("imports-typePluginHistory"),
            };
            return map[type] || type;
        },
        onLoaded(payload) {
            const hasActive = (payload.data || []).some((row) =>
                ["pending", "in_progress"].includes(row.status),
            );
            this.stopPolling();
            if (hasActive) {
                this.pollTimer = window.setTimeout(() => {
                    if (this.$refs.grid) {
                        this.$refs.grid.update();
                    }
                }, 4000);
            }
        },
        stopPolling() {
            if (this.pollTimer) {
                window.clearTimeout(this.pollTimer);
                this.pollTimer = null;
            }
        },
        makeCancelRequest(id) {
            this.idToCancel = id;
            this.$refs.confirmCancel.open();
        },
        cancelImport() {
            const options = keviApi.getOptions("import-history/" + this.idToCancel + "/cancel", "PUT");
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.$store.dispatch("notification/success", this.t("imports-cancelSuccess"));
                } else {
                    this.$store.dispatch("notification/warning", this.t("imports-cancelError"));
                }
                if (this.$refs.grid) {
                    this.$refs.grid.update();
                }
            });
        },
        openDetails(id) {
            const options = keviApi.getOptions("import-history/" + id, "GET");
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.detail = response.body.data;
                    this.$refs.details.open();
                }
            });
        },
        downloadFailedRows() {
            if (!this.detail || !this.detail.failed_rows) {
                return;
            }
            const rows = this.detail.failed_rows;
            const headers = Object.keys(rows[0] || {});
            const escape = (v) => `"${String(v === undefined || v === null ? "" : v).replace(/"/g, '""')}"`;
            const lines = [headers.map(escape).join(",")];
            rows.forEach((row) => {
                lines.push(headers.map((h) => escape(row[h])).join(","));
            });
            const csv = lines.join("\n");
            const element = document.createElement("a");
            element.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(csv));
            element.setAttribute("download", "failed-rows.csv");
            element.style.display = "none";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        },
    },
};
</script>

<style>
#imports-history-page .page-content {
    max-width: 100% !important;
}
</style>
